import { CommonModule } from '@angular/common';
import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { GenSingleImagePreviewData } from '../../../../../../../models/google-template/preview-interfaces/gen-single-image-preview-data.interface';
import { CreativeFormat, CreativeImageRouterHelper } from '../../../../../../../utils/creative-image-router-helper';
import { MobileFrameComponent } from '../../../preview-common/mobile-frame/mobile-frame.component';

/**
 * Layout for the Gmail EU mobile ad — the collapsed inbox row. EU ads have no
 * expanded state: the row links straight out, hence the external-link glyph.
 *
 * Presentational only, and not registered as a variant itself: the registered
 * per-CreativeFormat components wrap it and pass their own `creativeFormat`.
 */
@Component({
    selector: 'app-gen-gmail-ad-eu-mobile-shell',
    imports: [CommonModule, MobileFrameComponent],
    templateUrl: './gmail-ad-eu-mobile-shell.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './gmail-ad-eu-mobile-shell.component.scss'
})
export class GmailAdEuMobileShellComponent {
  @Input() data!: GenSingleImagePreviewData;

  /** Which uploaded creative fills the thumbnail. Set by the wrapping variant. */
  @Input() creativeFormat: CreativeFormat = 'square';

  /** Row count for the skeleton inbox filling out the list below the ad. */
  readonly inboxSkeletonRows = Array.from({ length: 5 });

  /** Thumbnail shapes, so each format's row reads as its own creative. */
  private static readonly THUMB_ASPECT: Record<CreativeFormat, string> = {
    landscape: '1200 / 628',
    square: '1 / 1',
    portrait: '960 / 1200',
  };

  get thumbAspectRatio(): string {
    return GmailAdEuMobileShellComponent.THUMB_ASPECT[this.creativeFormat];
  }

  /** The uploaded creative matching {@link creativeFormat}, if any. */
  get creativeImage(): string {
    return CreativeImageRouterHelper.resolve(this.data.images ?? [], this.creativeFormat);
  }

  /** Full dimensions, carried as the placeholder's tooltip. */
  get missingDimensionLabel(): string {
    return CreativeImageRouterHelper.labelFor(this.creativeFormat);
  }

  /** This slot is a thumbnail — too narrow for the pixel label, so it shows the ratio. */
  get missingDimensionShortLabel(): string {
    return CreativeImageRouterHelper.shortLabelFor(this.creativeFormat);
  }
}
